import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const MAX_ROOMS = 20;

const box = { display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', background: '#222', borderRadius: '6px', color: '#ddd', fontSize: '13px' };
const row = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' };
const input = { padding: '6px 8px', background: '#111', color: '#fff', border: '1px solid #444', borderRadius: '4px', fontSize: '13px' };
const button = { padding: '8px 10px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' };
const smallButton = { ...button, padding: '4px 8px' };
const danger = { ...button, background: '#d32f2f' };
const heading = { fontWeight: 'bold', marginTop: '6px' };
const muted = { color: '#888', fontSize: '12px' };

export default function BreakoutPanel({ roomId, participants, getSocket }) {
  const [roomCount, setRoomCount] = useState(3);
  const [mode, setMode] = useState('auto');
  const [minutes, setMinutes] = useState(0);
  const [assignments, setAssignments] = useState({}); // socketId -> room number (0 = main room)
  const [running, setRunning] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [roster, setRoster] = useState([]); // snapshot of students when rooms were started
  const [endsAt, setEndsAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [moveTo, setMoveTo] = useState({}); // socketId -> breakout room id, or 'main'
  const [message, setMessage] = useState('');
  const [inBreakout, setInBreakout] = useState(null); // label of the breakout room the host is in, or null

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return undefined;
    const onStarted = (d) => {
      setRunning(true);
      setRooms(d.rooms || []);
      setEndsAt(d.endsAt || null);
      toast.success('Breakout rooms started');
    };
    const onStatus = (d) => {
      if (!d.running) {
        setRunning(false);
        setInBreakout(null);
        return;
      }
      setRunning(true);
      setRooms(d.rooms || []);
      setEndsAt(d.endsAt || null);
      setInBreakout(d.currentLabel || null);
    };
    const onEnded = () => {
      setRunning(false);
      setRooms([]);
      setEndsAt(null);
      toast('Breakout rooms ended');
    };
    const onError = (d) => toast.error(d.message || 'Breakout rooms error');
    const onSent = (d) => toast.success(`Message sent to ${d.rooms} room(s)`);
    socket.on('breakout-started', onStarted);
    socket.on('breakout-status', onStatus);
    socket.on('breakout-ended', onEnded);
    socket.on('breakout-error', onError);
    socket.on('breakout-broadcast-sent', onSent);
    const onAssigned = (d) => setInBreakout(d.label || 'a breakout room');
    const onReturned = () => setInBreakout(null);
    socket.on('breakout-assigned', onAssigned);
    socket.on('breakout-returned', onReturned);
    socket.emit('breakout-status', { roomId });
    return () => {
      socket.off('breakout-started', onStarted);
      socket.off('breakout-status', onStatus);
      socket.off('breakout-ended', onEnded);
      socket.off('breakout-error', onError);
      socket.off('breakout-broadcast-sent', onSent);
      socket.off('breakout-assigned', onAssigned);
      socket.off('breakout-returned', onReturned);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // Countdown every second, and refresh room counts every 5 seconds while running
  useEffect(() => {
    if (!running) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(() => {
      const s = getSocket();
      if (s) s.emit('breakout-status', { roomId });
    }, 5000);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, roomId]);

  const emit = (event, payload) => {
    const s = getSocket();
    if (s) s.emit(event, { roomId, ...payload });
  };

  const start = () => {
    const count = Math.min(MAX_ROOMS, Math.max(1, Math.floor(Number(roomCount) || 1)));
    setRoster(participants);
    emit('breakout-start', {
      roomCount: count,
      mode,
      minutes: Math.max(0, Number(minutes) || 0),
      assignments,
    });
  };

  const move = (socketId) => {
    const target = moveTo[socketId] || 'main';
    emit('breakout-assign', { socketId, breakoutRoomId: target === 'main' ? null : target });
  };

  const visit = (target) => emit('breakout-visit', { breakoutRoomId: target });

  const broadcast = () => {
    const text = message.trim();
    if (!text) return;
    emit('breakout-broadcast', { message: text });
    setMessage('');
  };

  const end = () => emit('breakout-end', {});

  if (!running) {
    const roomOptions = Array.from({ length: Math.min(MAX_ROOMS, Math.max(1, Number(roomCount) || 1)) }, (_, i) => i + 1);
    return (
      <div style={box}>
        <div style={heading}>Create breakout rooms</div>
        <label style={row}>
          <span>Number of rooms</span>
          <input type="number" min="1" max={MAX_ROOMS} value={roomCount} onChange={(e) => setRoomCount(e.target.value)} style={{ ...input, width: 80 }} />
        </label>
        <label style={row}>
          <span>Assignment</span>
          <select value={mode} onChange={(e) => setMode(e.target.value)} style={input}>
            <option value="auto">Automatic (split evenly)</option>
            <option value="manual">Manual (I choose)</option>
          </select>
        </label>
        {mode === 'manual' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {participants.length === 0 && <div style={muted}>No participants in the main room yet.</div>}
            {participants.map((p) => (
              <label key={p.id} style={row}>
                <span>{p.name}</span>
                <select
                  value={assignments[p.id] || 0}
                  onChange={(e) => setAssignments({ ...assignments, [p.id]: Number(e.target.value) })}
                  style={input}
                >
                  <option value={0}>Main room</option>
                  {roomOptions.map((n) => (
                    <option key={n} value={n}>Room {n}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        )}
        <label style={row}>
          <span>Timer in minutes (0 = no timer)</span>
          <input type="number" min="0" max="180" value={minutes} onChange={(e) => setMinutes(e.target.value)} style={{ ...input, width: 80 }} />
        </label>
        <button type="button" style={button} onClick={start}>Start breakout rooms</button>
      </div>
    );
  }

  const list = roster.length ? roster : participants;
  const remaining = endsAt ? Math.max(0, Math.ceil((endsAt - now) / 1000)) : null;
  const clock = remaining !== null ? `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}` : null;

  return (
    <div style={box}>
      {inBreakout && (
        <div style={{ ...row, background: '#333', padding: '8px', borderRadius: '4px' }}>
          <span>You are in {inBreakout}</span>
          <button type="button" style={smallButton} onClick={() => visit(null)}>Return to Main Room</button>
        </div>
      )}
      <div style={row}>
        <span style={{ fontWeight: 'bold' }}>Breakout rooms running</span>
        {clock && <span>Time left {clock}</span>}
      </div>

      <div style={heading}>Rooms</div>
      {rooms.map((r) => (
        <div key={r.id} style={row}>
          <span>{r.label} ({r.count})</span>
          <button type="button" style={smallButton} onClick={() => visit(r.id)}>Visit</button>
        </div>
      ))}
      <div style={row}>
        <span>Main room</span>
        <button type="button" style={smallButton} onClick={() => visit(null)}>Visit</button>
      </div>

      <div style={heading}>Move participants</div>
      {list.map((p) => (
        <div key={p.id} style={row}>
          <span>{p.name}</span>
          <div style={{ display: 'flex', gap: '6px' }}>
            <select value={moveTo[p.id] || 'main'} onChange={(e) => setMoveTo({ ...moveTo, [p.id]: e.target.value })} style={input}>
              <option value="main">Main room</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
            <button type="button" style={smallButton} onClick={() => move(p.id)}>Move</button>
          </div>
        </div>
      ))}

      <div style={heading}>Message all breakout rooms</div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') broadcast(); }}
          placeholder="Type a message"
          style={{ ...input, flex: 1 }}
        />
        <button type="button" style={button} onClick={broadcast}>Send</button>
      </div>

      <button type="button" style={{ ...danger, marginTop: '6px' }} onClick={end}>End breakout rooms</button>
    </div>
  );
}
